import { React, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Edit, Delete } from '@mui/icons-material';
import Progress from '../Progress';
import AddGratuityConfigurationForm from '../forms/AddGratuityConfigurationForm';
import Popup from '../Popup';
import Notification from '../Notification';
import ConfirmDialog from '../ConfirmDialog';

const GratuityConfigurationTable = forwardRef((props, ref) => {
  const [openPopup, setOpenPopup] = useState(false);
  const [fetch_id, setFetchId] = useState(0);
  const [gratuityConfigs, setGratuityConfigs] = useState([]);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', subTitle: '' });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const pushNotify = (notification) => {
    if (props.onNotify) {
      props.onNotify(notification);
    } else {
      setNotify(notification);
    }
  };

  async function getAllGratuityConfigs() {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/configure/gratuity-configure/view`);
      const data = response.data?.data || response.data || [];
      setGratuityConfigs(Array.isArray(data) ? data : [data]);
    } catch (e) {
      if (e.response && e.response.status === 401) {
        pushNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error',
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else if (e.response && e.response.status === 404) {
        setGratuityConfigs([]);
      } else {
        pushNotify({
          isOpen: true,
          message: e.response?.data?.error_message || 'Failed to load gratuity configurations',
          type: 'error',
        });
        setGratuityConfigs([]);
      }
    }
    setLoading(false);
  }

  useImperativeHandle(ref, () => ({
    refreshTable: getAllGratuityConfigs,
  }));

  useEffect(() => {
    getAllGratuityConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.refreshKey && props.refreshKey > 0) {
      getAllGratuityConfigs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.refreshKey]);

  const handleDelete = async (delete_id) => {
    setConfirmDialog({
      ...confirmDialog,
      isOpen: false,
    });
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/configure/gratuity-configure/delete/${delete_id}`
      );
      if (response.status === 200 || response.status === 204) {
        pushNotify({
          isOpen: true,
          message: response.data?.message || 'Successfully deleted a record!',
          type: 'success',
        });
        getAllGratuityConfigs();
      }
    } catch (e) {
      if (e.response && e.response.status === 401) {
        pushNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error',
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        pushNotify({
          isOpen: true,
          message: e.response?.data?.error_message || 'Failed to delete',
          type: 'error',
        });
      }
    }
  };

  return (
    <>
      {loading ? (
        <Progress />
      ) : (
        <div style={{ width: '100%', padding: '10px 0', boxSizing: 'border-box' }}>
          <div style={{ width: '100%', backgroundColor: '#fff', borderRadius: '8px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginLeft: '80px' }} className="table">
              <thead className="table-dark">
                <tr>
                  <th scope="col" style={{ padding: '12px', textAlign: 'left' }}>
                    Config Key
                  </th>
                  <th scope="col" style={{ padding: '12px', textAlign: 'left' }}>
                    Config Value
                  </th>
                  <th scope="col" style={{ padding: '12px', textAlign: 'center' }}>
                    Status
                  </th>
                  <th scope="col" style={{ padding: '12px', textAlign: 'center', width: '200px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {gratuityConfigs.length > 0 ? (
                  gratuityConfigs.map((config, i) => {
                    const configId = config.id || config.fetch_id || i;
                    const statusValue = Number(config.status ?? 1) === 1 ? 'Active' : 'Inactive';
                    return (
                      <tr key={configId} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '12px', textAlign: 'left' }}>{config.config_key || '-'}</td>
                        <td style={{ padding: '12px', textAlign: 'left' }}>{config.config_value || '-'}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>{statusValue}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => {
                              setFetchId(configId);
                              setOpenPopup(true);
                            }}
                            style={{ marginRight: '8px' }}
                          >
                            <Edit /> Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() =>
                              setConfirmDialog({
                                isOpen: true,
                                title: 'Are you sure to delete this record?',
                                subTitle: "You can't undo this operation",
                                onConfirm: () => {
                                  handleDelete(configId);
                                },
                              })
                            }
                          >
                            <Delete /> Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                      No gratuity configurations found. Click "Add Gratuity Config" to create one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Popup
            title={fetch_id ? 'Edit Gratuity Configuration' : 'Add Gratuity Configuration'}
            openPopup={openPopup}
            setOpenPopup={(open) => {
              setOpenPopup(open);
              if (!open) {
                setFetchId(0);
              }
            }}
          >
            <AddGratuityConfigurationForm
              fetch_id={fetch_id}
              onRefresh={getAllGratuityConfigs}
              onNotify={props.onNotify}
              onCloseModal={() => {
                setOpenPopup(false);
                setFetchId(0);
              }}
              setOpenPopup={(open) => {
                setOpenPopup(open);
                if (!open) {
                  setFetchId(0);
                }
              }}
            />
          </Popup>
        </div>
      )}
      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </>
  );
});

export default GratuityConfigurationTable;
